import { zodResolver } from "@hookform/resolvers/zod";
import { Anchor, Button, Card, Divider, Image, Stack, Text, Title } from "@mantine/core";
import { CalendarBlankIcon, UserIcon } from "@phosphor-icons/react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import nextLogo from "@/assets/logo.png";
import { Input } from "@/components/@custom/Input";
import { SideBySide } from "@/components/@custom/SideBySide";
import { useRecaptcha } from "@/hooks";
import { useWindowLocationHost } from "@/hooks/useWindowLocationHost";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { GLOBAL_SCHEMA } from "@/lib/zod";
import { useAuth, useGetCompaniesQuery, useGetCompanyByHostQuery, useLazyGetSettingsByCompanyQuery, useLazyGetTermsUserByCompanyQuery } from "@/store/apis/nextcargo";
import { crypto } from "@/utils/cript";
import { cva } from "@/utils/cva.config";

type FormData = z.infer<typeof schema>;
const schema = z.object({
  cpf: GLOBAL_SCHEMA.CPF,
  birthDate: GLOBAL_SCHEMA.BIRTHDAY
});

export const Route = createFileRoute("/_auth/app/login")({
  component: DriverLogin,
  validateSearch: z.object({
    cpf: z.string().optional(),
    birthDate: z.string().optional()
  }),
  head: () => ({
    meta: [{ title: "Login - App" }]
  })
});

export function DriverLogin() {
  const { windowLocationHost, subDomain } = useWindowLocationHost();

  const { data: company } = useGetCompanyByHostQuery({ host: windowLocationHost }, { skip: !subDomain });
  const { data: companies } = useGetCompaniesQuery(undefined, { skip: !!subDomain });
  const [getTermsTrigger, { isLoading }] = useLazyGetTermsUserByCompanyQuery();
  const [settingsTrigger] = useLazyGetSettingsByCompanyQuery();

  const searchParams = Route.useSearch();

  const {
    SignIn,
    state: { signInDriver }
  } = useAuth();

  const {
    reset,
    handleSubmit: handleSubmitForm,
    control,
    formState: { isSubmitting }
  } = useForm<FormData>({
    mode: "all",
    resolver: zodResolver(schema),
    defaultValues: {
      cpf: searchParams.cpf ? crypto.decrypt(searchParams.cpf) : "",
      birthDate: searchParams.birthDate ? crypto.decrypt(searchParams.birthDate) : ""
    }
  });

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const { cpf, birthDate } = data;

    try {
      await SignIn("driver")({ cpf, birthDate, company: { id: String(company?.id) }, recaptcha_token: recaptcha });
      await getTermsTrigger({ id: Number(company?.id) });
    } catch {
      // Ignore Catch
    }

    reset();
  };

  const handleTermModal = () => {
    MODAL_HANDLER.terms();
  };

  useEffect(() => {
    if (company?.id) {
      (async () => {
        const { isLoading, data: settings, isSuccess } = await settingsTrigger({ id: company.id });

        if (!isLoading && isSuccess) {
          const { start, end, weekDays } = settings;
          let weekDaysParsed = JSON.parse(weekDays);
          weekDaysParsed = Object.values(weekDaysParsed);
          const week = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
          const selectedDays = week.filter((_, index: number) => weekDaysParsed[index] === true);

          localStorage.setItem(
            "@AppDriver:attendance",
            JSON.stringify({
              attendance: settings?.id || 0,
              attendanceStart: start,
              attendanceEnd: end,
              days: selectedDays
            })
          );
        }
      })();
    }
  }, [company]);

  const recaptcha = useRecaptcha();

  return (
    <>
      {company && (
        <>
          <SideBySide.Primary>
            <Image src={`${import.meta.env.VITE_APP_API_URL}/${company?.logo}`} fallbackSrc={nextLogo} className={`mb-2 block w-[300px]! md:hidden!`} />

            <Card shadow="lg" radius="lg" p="xl" withBorder>
              <Stack>
                <Title order={1} c="gray.8">
                  Entrar na sua conta
                </Title>

                <form onSubmit={handleSubmitForm(onSubmit)} className="flex flex-col gap-4">
                  <Stack>
                    <Input.Text
                      label="CPF"
                      description="Informe seu CPF"
                      name="cpf"
                      maskProps={{ mask: "999.999.999-99" }}
                      leftSection={<UserIcon weight="bold" />}
                      placeholder="CPF"
                      control={control}
                      withAsterisk
                    />

                    <Input.Text
                      label="Data de Nascimento"
                      description="Informe sua data de nascimento"
                      name="birthDate"
                      maskProps={{ mask: "99/99/9999" }}
                      leftSection={<CalendarBlankIcon weight="bold" />}
                      placeholder="Data de Nascimento"
                      control={control}
                      withAsterisk
                    />

                    <Button type="submit" color="primary" loading={signInDriver.isLoading || isLoading || isSubmitting} fullWidth>
                      Entrar
                    </Button>
                  </Stack>
                </form>

                <Text className="mt-2 text-center font-medium text-black">
                  Não possui cadastro?{" "}
                  <Link to={`/app/registro/$id`} params={{ id: company.id }} className={`text-primary hover:underline`}>
                    Cadastre-se.
                  </Link>
                </Text>

                <Divider />

                <Anchor className="max-w-72 cursor-pointer self-center text-center font-medium text-gray-400 hover:underline" role="button" onClick={handleTermModal}>
                  Leia nossa politica de Privacidade e <br /> Termos de Uso
                </Anchor>
              </Stack>
            </Card>
          </SideBySide.Primary>

          <SideBySide.Secondary>
            <Image src={`${import.meta.env.VITE_APP_API_URL}/${company?.logo}`} fallbackSrc={nextLogo} className={`w-[400px]! xl:w-[550px]!`} />
          </SideBySide.Secondary>
        </>
      )}

      {companies && (
        <div className={wrapper({ className: "mx-2 self-center p-6" })}>
          <div className="flex flex-wrap-reverse items-center justify-center gap-4 xl:justify-between">
            <div className="flex flex-[1_1_min(100%,200px)] flex-col items-center justify-center gap-3">
              <h1 className="text-center text-3xl font-semibold">Selecione uma empresa: </h1>

              <div className="flex flex-wrap justify-center gap-2">
                {companies?.map((item) => {
                  return (
                    <a href={item.website} key={item.id} className="bg-primary rounded-full px-6 py-3 text-lg font-medium text-white uppercase transition hover:bg-orange-600">
                      {item.company}
                    </a>
                  );
                })}
              </div>
            </div>

            <div className="*:w-[400px] xl:flex-1">
              <img src={company?.logo ? `${import.meta.env.VITE_APP_API_URL}/${company?.logo}` : nextLogo} alt="" className={`block h-auto max-w-full xl:w-[550px]`} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const wrapper = cva({
  base: "container rounded-xl bg-white"
});
